import React, { useContext } from 'react';
import Skeleton from 'react-loading-skeleton';
import dayjs from 'dayjs';
import isEmpty from 'lodash/isEmpty';
import styled from 'styled-components';
import { Card, Empty, EmptyTable, HeaderCell, HeaderRow, Label, PaginationFooter, Section, SortLabel, Status, Table, TableBody, TableHead, Text } from 'components';
import { theme, toRem } from 'design';
import { Appearance } from 'design/types';
import ListContext from 'providers/ListContextProvider';
import { IPaymentSet, IPayoutStateByDate, PayoutStatus } from 'types';
import { sumBy } from 'utils/arrays';
import { centsToDollarsDisplay, centsToNegativeDollarsDisplay } from 'utils/currency';
import { getTimeWithZone } from 'utils/date';
import OverviewTabChart from './OverviewTabChart';
import { ChartSectionHeader, PaymentSetRow } from './styles';

interface IIUIProps {
  isLoading?: boolean;
  statsLoading?: boolean;
  paymentSets?: IPaymentSet[];
  onNavigateToPaymentDetails: (id: string) => void;
  paymentStats: IPayoutStateByDate[];
  businessDaySection: React.ReactNode;
  recordsPerPage: number;
  grossTotal: number;
  holdbackTotal: number;
  netTotal: number;
  instantTotal: number;
  payrollTotal: number;
  paidCount?: number;
  pendingCount?: number;
  canceledCount?: number;
  expiredCount?: number;
  paidTotal?: number;
  pendingTotal?: number;
  canceledTotal?: number;
  expiredTotal?: number;
}

const StyledSection = styled(Section)`
  padding: 0 0 0 ${toRem(theme.layout.gutter.small)};
  z-index: 100;

  > div {
    padding-right: ${toRem(theme.layout.gutter.small)};
  }

  @media (min-width: ${theme.breakpoint.med}px) {
    padding: 0 0 0 ${toRem(theme.layout.gutter.large)};

    > div {
      padding-right: ${toRem(theme.layout.gutter.large)};
    }

    &.chart-section {
      margin-top: ${toRem(-66)};
    }
  }
`;

const ChartCard = styled(Card)`
  flex-flow: column nowrap;
  overflow: visible;
`;

const PaymentTotals = styled.div`
  display: flex;
  flex-flow: row nowrap;
  margin-top: ${toRem(32)};
  width: 100%;
`;

const TotalSection = styled.div`
  display: flex;
  flex-flow: column nowrap;
  width: 100%;
  align-items: center;

  label {
    text-transform: uppercase;
    font-size: ${toRem(11)};
  }

  @media (min-width: ${theme.breakpoint.med}px) {
    &:first-child {
      border-right: ${toRem(1)} solid ${theme.color.neutral[200]};
    }
  }
`;

const TotalRow = styled.div`
  display: flex;
  flex-flow: column nowrap;
  width: 100%;
  margin-top: ${toRem(16)};
  height: 100%;
  justify-content: space-between;

  @media (min-width: ${theme.breakpoint.med}px) {
    flex-flow: row nowrap;
  }

  label,
  .status-label {
    text-transform: capitalize;
    font-size: ${toRem(11)};
    font-weight: ${theme.font.weight.regular};
    color: ${theme.color.neutral[500]};
  }
`;

const TotalValue = styled.div`
  display: flex;
  flex-flow: column nowrap;
  width: 100%;
  align-items: center;

  span.value {
    margin-top: ${toRem(4)};
  }

  p {
    font-size: ${toRem(16)};
    margin-top: ${toRem(4)};
    font-weight: ${theme.font.weight.semiBold};
  }
`;

export const OverviewTab = ({
  isLoading,
  statsLoading,
  paymentSets,
  onNavigateToPaymentDetails,
  paymentStats,
  businessDaySection,
  recordsPerPage,
  grossTotal,
  holdbackTotal,
  netTotal,
  instantTotal,
  payrollTotal,
  paidCount,
  pendingCount,
  canceledCount,
  expiredCount,
  paidTotal,
  pendingTotal,
  canceledTotal,
  expiredTotal,
}: IIUIProps) => {
  const paymentsByDateCountTotal = paymentStats ? sumBy(paymentStats, 'count') : '--';

  const paymentListContext = useContext(ListContext);
  const { pagination: Pager, resultsPerPage: ResultsPerPage } = paymentListContext.components;
  const { onSortChange, isSortedBy, isSortDesc } = paymentListContext.methods;
  const sortDesc = isSortDesc();
  return (
    <>
      <StyledSection className="chart-section" noContentBorder data-cy="payments-chart-section">
        <ChartCard>
          <ChartSectionHeader title="Payments" value={`(${paymentsByDateCountTotal})`} isLoading={isLoading}>
            {businessDaySection}
          </ChartSectionHeader>

          <OverviewTabChart paymentStats={paymentStats} isLoading={statsLoading} />
          <PaymentTotals data-cy="payment-totals">
            <TotalSection>
              {isLoading ? <Skeleton width={100} /> : <Label>Payment Overview</Label>}

              <TotalRow>
                {isLoading ? (
                  <TotalValue>
                    <Skeleton width={100} />
                    <Skeleton width={100} className="value" />
                  </TotalValue>
                ) : (
                  <TotalValue data-cy="stats-gross">
                    <Label>Gross</Label>
                    <Text size="small">{centsToDollarsDisplay(grossTotal)}</Text>
                  </TotalValue>
                )}
                {isLoading ? (
                  <TotalValue>
                    <Skeleton width={100} />
                    <Skeleton width={100} className="value" />
                  </TotalValue>
                ) : (
                  <TotalValue data-cy="stats-holdback">
                    <Label>Holdback</Label>
                    <Text size="small">{centsToNegativeDollarsDisplay(holdbackTotal)}</Text>
                  </TotalValue>
                )}
                {isLoading ? (
                  <TotalValue>
                    <Skeleton width={100} />
                    <Skeleton width={100} className="value" />
                  </TotalValue>
                ) : (
                  <TotalValue data-cy="stats-net">
                    <Label>Net</Label>
                    <Text size="small">{centsToDollarsDisplay(netTotal)}</Text>
                  </TotalValue>
                )}
                {isLoading ? (
                  <TotalValue>
                    <Skeleton width={100} />
                    <Skeleton width={100} className="value" />
                  </TotalValue>
                ) : (
                  <TotalValue data-cy="stats-payroll">
                    <Label>Payroll</Label>
                    <Text size="small">{centsToDollarsDisplay(payrollTotal)}</Text>
                  </TotalValue>
                )}
                {isLoading ? (
                  <TotalValue>
                    <Skeleton width={100} />
                    <Skeleton width={100} className="value" />
                  </TotalValue>
                ) : (
                  <TotalValue data-cy="stats-instant">
                    <Label>Instant</Label>
                    <Text size="small">{centsToDollarsDisplay(instantTotal)}</Text>
                  </TotalValue>
                )}
              </TotalRow>
            </TotalSection>
            <TotalSection>
              {isLoading ? <Skeleton width={100} /> : <Label>Payment Status</Label>}
              <TotalRow>
                {isLoading ? (
                  <TotalValue>
                    <Skeleton width={100} />
                    <Skeleton width={100} className="value" />
                  </TotalValue>
                ) : (
                  <TotalValue data-cy="status-paid">
                    <Status appearance={Appearance.SUCCESS} label={`Paid (${paidCount || 0})`} isLoading={isLoading} className="status-label" />
                    <Text size="small">{centsToDollarsDisplay(paidTotal || 0)}</Text>
                  </TotalValue>
                )}
                {isLoading ? (
                  <TotalValue>
                    <Skeleton width={100} />
                    <Skeleton width={100} className="value" />
                  </TotalValue>
                ) : (
                  <TotalValue data-cy="status-pending">
                    <Status appearance={Appearance.WARNING} label={`Pending (${pendingCount || 0})`} isLoading={isLoading} className="status-label" />
                    <Text size="small">{centsToDollarsDisplay(pendingTotal || 0)}</Text>
                  </TotalValue>
                )}
                {isLoading ? (
                  <TotalValue>
                    <Skeleton width={100} />
                    <Skeleton width={100} className="value" />
                  </TotalValue>
                ) : (
                  <TotalValue data-cy="status-canceled">
                    <Status appearance={Appearance.NEUTRAL} label={`Canceled (${canceledCount || 0})`} isLoading={isLoading} className="status-label" />
                    <Text size="small">{centsToDollarsDisplay(canceledTotal || 0)}</Text>
                  </TotalValue>
                )}
                {isLoading ? (
                  <TotalValue>
                    <Skeleton width={100} />
                    <Skeleton width={100} className="value" />
                  </TotalValue>
                ) : (
                  <TotalValue data-cy="status-expired">
                    <Status appearance={Appearance.DANGER} label={`Expired (${expiredCount || 0})`} isLoading={isLoading} className="status-label" />
                    <Text size="small">{centsToDollarsDisplay(expiredTotal || 0)}</Text>
                  </TotalValue>
                )}
              </TotalRow>
            </TotalSection>
          </PaymentTotals>
        </ChartCard>
      </StyledSection>

      <StyledSection data-cy="table">
        <Table scrollable sticky cellMinWidth={100} data-cy="payment-set-table">
          <TableHead>
            <HeaderRow sticky>
              <HeaderCell onClick={() => onSortChange('payment_date')} data-cy="header-payment-date">
                <SortLabel label="Business Day" isSorted={isSortedBy('payment_date')} isSortedDesc={sortDesc} isLoading={isLoading} />
              </HeaderCell>

              <HeaderCell align="right" onClick={() => onSortChange('totals.net')} data-cy="header-totals">
                <SortLabel label="Net Paid" isSorted={isSortedBy('totals.net')} isSortedDesc={sortDesc} isLoading={isLoading} />
              </HeaderCell>

              <HeaderCell>{isLoading ? '' : 'Status'}</HeaderCell>

              <HeaderCell onClick={() => onSortChange('created_by.name')} data-cy="header-sent-by">
                <SortLabel label="Sent By" isSorted={isSortedBy('created_by.name')} isSortedDesc={sortDesc} isLoading={isLoading} />
              </HeaderCell>

              <HeaderCell data-cy="header-payment-set-id">{isLoading ? '' : 'Set ID'}</HeaderCell>

              <HeaderCell onClick={() => onSortChange('description')} data-cy="header-description">
                {isLoading ? '' : 'Description'}
              </HeaderCell>
            </HeaderRow>
          </TableHead>

          <TableBody>
            {isLoading
              ? [...Array(recordsPerPage)].map((e, i) => <PaymentSetRow key={i} isLoading />)
              : paymentSets?.map((paymentSet: IPaymentSet, index: number) => {
                  const getCountForStatus = (status: PayoutStatus) => (paymentSet.totals?.status || []).find((s) => s.type === status)?.count;
                  return (
                    <PaymentSetRow
                      key={`payment-set-row-${index}`}
                      setID={paymentSet.id}
                      businessDay={paymentSet.paymentDate}
                      submittedDate={dayjs(paymentSet.createdAt).format('YYYY-MM-DD')}
                      submittedTime={getTimeWithZone(paymentSet.createdAt)}
                      sentBy={paymentSet.createdBy?.name}
                      description={paymentSet?.description}
                      statusPaid={getCountForStatus(PayoutStatus.PAID)}
                      statusPending={getCountForStatus(PayoutStatus.PENDING)}
                      statusExpired={getCountForStatus(PayoutStatus.EXPIRED)}
                      statusCanceled={getCountForStatus(PayoutStatus.CANCELED)}
                      statusPayroll={getCountForStatus(PayoutStatus.PAYROLL)}
                      totals={paymentSet.totals}
                      isLoading={!paymentSet}
                      onClick={() => onNavigateToPaymentDetails(paymentSet.id)}
                      data-cy="payment-set-row"
                      data-value={paymentSet.id}
                    />
                  );
                })}
            {!isLoading && isEmpty(paymentSets) && (
              <EmptyTable>
                <Empty icon="Payment" message="No payments found." />
              </EmptyTable>
            )}
          </TableBody>
        </Table>

        <PaginationFooter>
          {!isLoading && (
            <>
              {ResultsPerPage}
              {Pager}
            </>
          )}
        </PaginationFooter>
      </StyledSection>
    </>
  );
};
